"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, Calendar, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackPosts = [{
  id: "sample-1",
  title: "Thông báo về việc tiếp nhận hồ sơ đăng ký hội viên năm 2025",
  date: "2025-01-15",
  excerpt: "Văn phòng Hội thông báo tiếp nhận hồ sơ đăng ký gia nhập của các nhà khoa học, chuyên gia địa lý môi trường trên cả nước theo điều lệ quy định.",
  link: "/NewsAndEventsPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/sample_news_notice.png",
  categoryName: "Thông báo"
}, {
  id: "sample-2",
  title: "Kế hoạch tọa đàm khoa học về thích ứng biến đổi khí hậu khu vực ven biển",
  date: "2025-02-10",
  excerpt: "Chương trình chuẩn bị tổ chức hội thảo chuyên đề đánh giá diễn biến địa mạo và bảo tồn tài nguyên đất đai tại các tỉnh đồng bằng duyên hải.",
  link: "/NewsAndEventsPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/sample_news_symposium.png",
  categoryName: "Hội thảo"
}, {
  id: "sample-3",
  title: "Tổng hợp các định hướng nghiên cứu địa lý môi trường giai đoạn mới",
  date: "2025-02-28",
  excerpt: "Bản ghi nhận các phương hướng trọng tâm phục vụ quản lý môi trường bền vững theo các quyết định thành lập và phê duyệt điều lệ của Bộ Nội vụ.",
  link: "/NewsAndEventsPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/sample_news_research.png",
  categoryName: "Nghiên cứu"
}];
function NewsFeedConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    isRefetching
  } = usePosts();
  const displayPosts = posts && posts.length > 0 ? posts : fallbackPosts;
  return <div className="flex flex-col gap-10">
      {/* Advisory Placard */}
      <div className="flex items-start gap-3.5 rounded-md border border-border bg-card p-4 text-card-foreground shadow-xs sm:p-5">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-secondary" />
        <div className="space-y-1 text-sm leading-relaxed">
          <p className="font-semibold text-foreground">
            Thông báo về cổng lưu trữ thông tin điện tử
          </p>
          <p className="text-muted-foreground">
            Các nội dung hiển thị ban đầu là dữ liệu mẫu phục vụ bố cục hiển thị
            trong giai đoạn chuẩn bị phê duyệt nội dung chính thức từ Ban Thường
            trực Hội Địa lý Môi trường Việt Nam. Mọi thông báo pháp lý và lịch sự
            kiện chính xác sẽ được công bố định kỳ theo văn bản của Hội.
          </p>
        </div>
      </div>

      {/* Loading state skeleton */}
      {loading ? <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-md border border-border bg-card shadow-xs">
              <div className="h-48 w-full animate-pulse bg-muted" />
              <div className="flex flex-1 flex-col gap-3 p-5">
                <div className="h-4 w-24 animate-pulse rounded-sm bg-muted" />
                <div className="h-6 w-5/6 animate-pulse rounded-sm bg-muted" />
                <div className="h-16 w-full animate-pulse rounded-sm bg-muted" />
                <div className="mt-auto pt-4">
                  <div className="h-8 w-28 animate-pulse rounded-sm bg-muted" />
                </div>
              </div>
            </div>)}
        </div> : error ? <div className="rounded-md border border-destructive/30 bg-card p-6 text-center text-card-foreground">
          <p className="text-muted-foreground">
            Không thể tải dữ liệu bài viết lúc này.
          </p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
            Tải lại bài viết
          </Button>
        </div> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {displayPosts.map((item, i) => {
        const rawDate = item.date || "2025-01-01";
        const formattedDate = new Date(rawDate).toLocaleDateString("vi-VN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        });
        const destination = item.link || "/NewsAndEventsPage";
        const categoryText = (item as any).categories?.[0]?.name || (item as any).categoryName || "Thông báo Hội";
        const postImg = item.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1803866/img/news_general_thumbnail.png";
        return <Card key={item.id || i} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-md border border-border bg-card p-0 shadow-xs transition-colors duration-200 hover:border-secondary">
                <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                  <img src={postImg} alt={item.title || "Hình ảnh bài viết"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="secondary" className="rounded-sm bg-card/95 text-xs font-medium text-foreground backdrop-blur-xs">
                      {categoryText}
                    </Badge>
                  </div>
                </div>

                <CardContent className="flex flex-1 flex-col p-5">
                  <div className="mb-2.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <SafeHydrate><Calendar className="size-3.5 text-secondary" /></SafeHydrate>
                    <time dateTime={rawDate}>{formattedDate}</time>
                  </div>

                  <h3 className="line-clamp-2 text-base font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary">
                    {item.title}
                  </h3>

                  {item.excerpt && <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {item.excerpt.replace(/<[^>]+>/g, "")}
                    </p>}

                  <div className="mt-auto pt-5">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between rounded-sm border-border text-foreground hover:bg-accent hover:text-accent-foreground">
                      <Link to={destination}>
                        <span>Xem toàn văn</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>;
      })}
        </div>}

      {/* Pagination controls */}
      <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
        <div className="text-xs text-muted-foreground">
          Trang {currentPage || 1} trên {totalPages || 1}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev || isRefetching} className="rounded-sm border-border">
            <ChevronLeft className="size-4" />
            <span>Trang trước</span>
          </Button>

          <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext || isRefetching} className="rounded-sm border-border">
            <span>Trang sau</span>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>;
}
export default function News() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="danh-sách-tin-tức-và-sự-kiện" data-nav="light" className="bg-background py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header container with institutional dual-bar anchor motif */}
        <div className="mb-10 max-w-3xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-sm bg-accent text-primary">
              <FileText className="size-4" />
            </span>
            <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Cổng thông tin xuất bản
            </span>
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-secondary sm:text-3xl">
            Danh sách tin tức và sự kiện
          </h1>

          <div className="mt-3.5 flex items-center gap-2">
            <div className="h-1 w-12 rounded-full bg-secondary" />
            <div className="h-px flex-1 bg-border" />
          </div>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Thông tin hoạt động khoa học, kế hoạch tổ chức hội nghị và thông báo
            hành chính từ Ban Thường trực Hội Địa lý Môi trường Việt Nam và các chi
            hội địa phương.
          </p>
        </div>

        {/* Dynamic Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <NewsFeedConsumer />
        </PostsProvider>
      </div>
    </section>;
}